import React, { useState } from 'react';
import API from '../../../utils/api';

const BLUE = '#1B9DD9';
const NAVY = '#0E2A55';
const ORANGE = '#F39200';

const fieldWrapStyle = { display: 'block', position: 'relative', textAlign: 'left', marginBottom: 22 };
const floatingLabelStyle = {
    position: 'absolute', top: -9, left: 11, background: '#fff', padding: '0 6px',
    fontSize: 12, fontWeight: 600, color: NAVY, lineHeight: 1, zIndex: 1
};

// Defined at module scope (not inside LoginPage) so its identity stays stable across
// re-renders — otherwise React remounts the input on every keystroke and it loses focus.
const Field = ({ label, required, children }) => (
    <label style={fieldWrapStyle}>
        <span style={floatingLabelStyle}>
            {required && <span style={{ color: '#e2483a' }}>* </span>}{label}
        </span>
        {children}
    </label>
);

// DEMO_USERS removed

export default function LoginPage({ onLogin }) {
    const [mode, setMode] = useState('login'); // 'login' or 'register' or 'forgot'
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role] = useState('customer'); // Always customer for public signup
    const [showPwd, setShowPwd] = useState(false);
    const [remember, setRemember] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [phone, setPhone] = useState('');
    const [token, setToken] = useState('');

    React.useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const vt = urlParams.get('verify');
        const rt = urlParams.get('token');

        if (vt) {
            handleVerify(vt);
        } else if (rt) {
            setToken(rt);
            setMode('reset-password');
        }
    }, []);

    const handleVerify = async (vtoken) => {
        setLoading(true);
        try {
            await API.post('/auth/verify', { token: vtoken });
            setError('✅ Email verified successfully! You can now login.');
            setLoading(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Verification failed. Link might be expired.');
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');
        if (!password) { setError('Please enter a new password'); return; }
        if (password.length < 8 || !/[A-Z]/.test(password)) {
            setError('Password must be 8+ chars & include an uppercase letter');
            return;
        }
        setLoading(true);
        try {
            await API.post('/auth/reset-password', { token, newPassword: password });
            setError('✅ Password reset successfully! Redirecting to login...');
            setTimeout(() => {
                setMode('login');
                setError('');
                setPassword('');
            }, 3000);
            setLoading(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password.');
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        if (mode === 'reset-password') return handleResetPassword(e);
        e.preventDefault();
        setError('');

        if (mode === 'forgot') {
            if (!email) { setError('Please enter your email to reset password'); return; }
            setLoading(true);
            try {
                await API.post('/auth/forgot-password', { email });
                setError('✅ Password reset link has been sent to your email.');
                setLoading(false);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to send reset email.');
                setLoading(false);
            }
            return;
        }

        if (mode === 'register') {
            if (!name || !companyName || !phone) { setError('Please fill in all required fields'); return; }
            if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
            if (!/[A-Z]/.test(password)) { setError('Password must contain at least one uppercase letter'); return; }
        }

        setLoading(true);
        try {
            const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
            const payload = mode === 'login'
                ? { email, password }
                : { name, email, password, role, company_name: companyName, phone };

            const { data } = await API.post(endpoint, payload);

            if (mode === 'register') {
                setError('✅ ' + (data.message || 'Account created successfully! Please login.'));
                setName('');
                setEmail('');
                setPassword('');
                setMode('login');
                setLoading(false);
                return;
            }

            // Persist user info (including token) for session
            localStorage.setItem('userInfo', JSON.stringify(data));
            onLogin(data.role, data);
        } catch (err) {
            setError(err.response?.data?.message || 'Something went wrong. Please try again.');
            setLoading(false);
        }
    };

    const inputStyle = {
        width: '100%', boxSizing: 'border-box', height: 46, borderRadius: 8,
        border: '1px solid #cbd6e2', background: '#fff', padding: '0 15px',
        fontSize: 15, fontWeight: 500, color: NAVY, fontFamily: 'inherit', outline: 'none',
        transition: 'border-color .15s, box-shadow .15s'
    };
    const focusOn = e => { e.target.style.borderColor = BLUE; e.target.style.boxShadow = `0 0 0 3px rgba(27,157,217,.14)`; };
    const focusOff = e => { e.target.style.borderColor = '#cbd6e2'; e.target.style.boxShadow = 'none'; };

    return (
        <div style={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <style>{`
                @keyframes lf-drive { 0%,48%{transform:translateX(0);opacity:1} 74%,100%{transform:translateX(360px);opacity:0} }
                @keyframes lf-roll { 0%,48%{transform:rotate(0)} 70%,100%{transform:rotate(720deg)} }
                @keyframes lf-load { 0%,14%{transform:translate(0,0) scale(1);opacity:1} 34%{transform:translate(40px,-28px) scale(1);opacity:1} 46%,48%{transform:translate(77px,-31px) scale(1);opacity:1} 74%,100%{transform:translate(437px,-31px) scale(1);opacity:0} }
                @keyframes lf-pulse { 0%{transform:scale(.7);opacity:.55} 70%{opacity:0} 100%{transform:scale(1.5);opacity:0} }
                @keyframes lf-pop { 0%{transform:scale(0);opacity:0} 60%{transform:scale(1.12)} 100%{transform:scale(1);opacity:1} }
                @keyframes lf-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
                @media (max-width: 900px) { .lf-illo { display: none !important; } }
                .lf-input::placeholder { color: #a7b2c2; opacity: 1; }
            `}</style>

            {/* ─── Left Side: Form ─── */}
            <div style={{
                flex: '1 1 45%', display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', textAlign: 'center', padding: '32px 40px', boxSizing: 'border-box',
                background: '#fff', overflowY: 'auto'
            }}>
                <div style={{ width: '100%', maxWidth: 416 }}>

                    {mode === 'login' ? (
                        <>
                            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.28em', color: BLUE, textTransform: 'uppercase', marginBottom: 12 }}>
                                Welcome to
                            </div>
                            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Allin Logistics" style={{ width: 120, height: 'auto', display: 'block', margin: '0 auto 12px' }} />
                            <p style={{ fontSize: 15, fontWeight: 600, color: NAVY, margin: '0 0 22px' }}>Let's get things moving 📦</p>
                        </>
                    ) : (
                        <>
                            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Allin Logistics" style={{ width: 100, height: 'auto', display: 'block', margin: '0 auto 12px' }} />
                            <p style={{ fontSize: 12, color: '#8691b0', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 4px' }}>
                                {mode === 'forgot' ? 'Reset Password' : mode === 'reset-password' ? 'Set New Password' : 'Create Account'}
                            </p>
                            <p style={{ fontSize: 14, color: '#8691b0', fontWeight: 400, margin: '0 0 20px' }}>
                                {mode === 'forgot' ? 'Enter your email to receive a reset link' : mode === 'reset-password' ? 'Please enter your new secure password' : 'Sign up to start managing your cargo'}
                            </p>
                        </>
                    )}

                    {/* Success/Error Message */}
                    {error && (
                        <div style={{
                            padding: '10px 14px', borderRadius: 8, textAlign: 'left',
                            background: error.includes('✅') ? '#f0fdf4' : '#fff5f5',
                            border: `1px solid ${error.includes('✅') ? '#bbf7d0' : '#fecaca'}`,
                            color: error.includes('✅') ? '#15803d' : '#dc2626',
                            fontSize: 13, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8
                        }}>
                            <span>{error.includes('✅') ? '✓' : '⚠'}</span> {error}
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {mode === 'register' && (
                            <>
                                <Field label="Full Name" required>
                                    <input type="text" value={name} onChange={e => setName(e.target.value)}
                                        onFocus={focusOn} onBlur={focusOff}
                                        placeholder="Enter your name" className="lf-input" style={inputStyle} />
                                </Field>
                                <Field label="Company Name" required>
                                    <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)}
                                        onFocus={focusOn} onBlur={focusOff}
                                        placeholder="Enter company name" className="lf-input" style={inputStyle} />
                                </Field>
                                <Field label="Phone Number" required>
                                    <input type="text" value={phone} onChange={e => setPhone(e.target.value)}
                                        onFocus={focusOn} onBlur={focusOff}
                                        placeholder="+1 234..." className="lf-input" style={inputStyle} />
                                </Field>
                            </>
                        )}

                        {(mode === 'login' || mode === 'register' || mode === 'forgot') && (
                            <Field label="Email" required>
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                                    onFocus={focusOn} onBlur={focusOff}
                                    placeholder="m@example.com" className="lf-input" style={inputStyle} />
                            </Field>
                        )}

                        {mode !== 'forgot' && (
                            <Field label={mode === 'reset-password' ? 'New Password' : 'Password'} required>
                                <div style={{ position: 'relative' }}>
                                    <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                                        onFocus={focusOn} onBlur={focusOff}
                                        placeholder="••••••••" className="lf-input" style={{ ...inputStyle, paddingRight: 44 }} />
                                    <span onClick={() => setShowPwd(!showPwd)} style={{
                                        position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                                        cursor: 'pointer', color: '#9aa9bd', userSelect: 'none', display: 'flex'
                                    }}>
                                        {showPwd ? (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                                            </svg>
                                        ) : (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" />
                                            </svg>
                                        )}
                                    </span>
                                </div>
                            </Field>
                        )}

                        {mode === 'login' && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '2px 0 20px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                                        style={{ width: 16, height: 16, accentColor: BLUE, cursor: 'pointer' }} />
                                    <span style={{ fontSize: 13, color: '#5a6b82', fontWeight: 500 }}>Remember me</span>
                                </label>
                                <span onClick={() => { setMode('forgot'); setError(''); }} style={{ fontSize: 13, color: BLUE, fontWeight: 600, cursor: 'pointer' }}>Forgot password?</span>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button type="submit" disabled={loading}
                            style={{
                                width: '100%', height: 48, borderRadius: 10, border: 'none', cursor: loading ? 'wait' : 'pointer',
                                fontSize: 16, fontWeight: 700, color: '#fff',
                                background: loading ? '#8fbdd6' : BLUE,
                                boxShadow: `0 16px 34px -18px rgba(27,157,217,.8)`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                transition: 'background-color .15s', marginTop: mode === 'register' ? 6 : 0
                            }}>
                            {loading ? 'Processing...' : (
                                <>
                                    {mode === 'login' ? 'Log in' : mode === 'forgot' ? 'Send Reset Link' : mode === 'reset-password' ? 'Update Password' : 'Create Account'}
                                    {(mode === 'login' || mode === 'register') && (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                                        </svg>
                                    )}
                                </>
                            )}
                        </button>
                    </form>

                    <p style={{ fontSize: 13, color: '#8592a4', margin: '20px 0 4px' }}>
                        © 2026 All In Logistics.&nbsp;&bull;&nbsp;
                        {mode === 'login' ? "Don't have an account? " : mode === 'forgot' ? 'Remember your password? ' : 'Already have an account? '}
                        <span onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
                            style={{ color: BLUE, fontWeight: 600, cursor: 'pointer' }}>
                            {mode === 'login' ? 'Sign up' : 'Login instead'}
                        </span>
                    </p>
                </div>
            </div>

            {/* ─── Right Side: Illustration ─── */}
            <div className="lf-illo" style={{
                flex: '1 1 55%', position: 'relative', overflow: 'hidden',
                background: 'radial-gradient(120% 90% at 62% 34%, #eaf4fb 0%, #f4f9fd 55%, #ffffff 100%)'
            }}>
                <div style={{ position: 'absolute', right: 70, top: 60, width: 280, height: 280, borderRadius: '50%', background: BLUE, opacity: .08, animation: 'lf-float 7s ease-in-out infinite' }} />
                <div style={{ position: 'absolute', left: 40, bottom: 70, width: 130, height: 130, borderRadius: '50%', border: `2px solid rgba(27,157,217,.16)`, animation: 'lf-float 6s ease-in-out infinite' }} />
                <div style={{ position: 'absolute', right: 40, top: 40, display: 'grid', gridTemplateColumns: 'repeat(4, 7px)', gap: 12, opacity: .5 }}>
                    {[BLUE, '#bcdff2', '#bcdff2', ORANGE, '#bcdff2', BLUE, '#bcdff2', '#bcdff2'].map((c, i) => (
                        <span key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: c }} />
                    ))}
                </div>

                <svg viewBox="0 0 580 480" style={{ position: 'absolute', left: '50%', top: '52%', transform: 'translate(-50%,-50%)', width: 'min(560px,86%)', height: 'auto', overflow: 'visible' }}>
                    <ellipse cx="185" cy="392" rx="150" ry="16" fill={NAVY} opacity="0.07" />
                    <ellipse cx="430" cy="392" rx="110" ry="14" fill={NAVY} opacity="0.07" />

                    <g style={{ animation: 'lf-float 8s ease-in-out infinite', transformBox: 'fill-box', transformOrigin: 'center' }}>
                        <rect x="52" y="168" width="278" height="210" rx="14" fill="#fff" stroke={NAVY} strokeWidth="4" />
                        <path d="M36 172 L191 96 L346 172 Z" fill={BLUE} stroke={NAVY} strokeWidth="4" strokeLinejoin="round" />
                        <path d="M191 96 L346 172 L191 172 Z" fill={NAVY} opacity=".14" />
                        <rect x="40" y="168" width="302" height="10" rx="4" fill={NAVY} />
                        <circle cx="191" cy="140" r="15" fill="#cfe6f6" stroke={NAVY} strokeWidth="3" />
                        <line x1="176" y1="140" x2="206" y2="140" stroke={NAVY} strokeWidth="2" />
                        <line x1="191" y1="125" x2="191" y2="155" stroke={NAVY} strokeWidth="2" />

                        <rect x="70" y="212" width="106" height="166" rx="4" fill="none" stroke={NAVY} strokeWidth="3" />
                        <line x1="123" y1="212" x2="123" y2="378" stroke={NAVY} strokeWidth="3" />
                        <line x1="70" y1="268" x2="176" y2="268" stroke={NAVY} strokeWidth="3" />
                        <line x1="70" y1="324" x2="176" y2="324" stroke={NAVY} strokeWidth="3" />
                        <rect x="80" y="234" width="34" height="30" rx="3" fill={ORANGE} />
                        <rect x="132" y="238" width="34" height="26" rx="3" fill={BLUE} />
                        <rect x="80" y="292" width="34" height="28" rx="3" fill="#dce4ec" />
                        <rect x="132" y="290" width="34" height="30" rx="3" fill={ORANGE} />
                        <rect x="80" y="346" width="34" height="28" rx="3" fill={BLUE} />
                        <rect x="132" y="348" width="34" height="26" rx="3" fill="#dce4ec" />

                        <rect x="248" y="230" width="76" height="148" rx="5" fill={NAVY} />
                        <rect x="242" y="214" width="88" height="18" rx="5" fill={BLUE} />
                    </g>

                    <g style={{ animation: 'lf-load 7s ease-in-out infinite', transformBox: 'fill-box', transformOrigin: 'center' }}>
                        <rect x="266" y="322" width="44" height="44" rx="5" fill={ORANGE} />
                        <rect x="266" y="322" width="44" height="14" rx="5" fill="#ffab33" />
                        <line x1="288" y1="322" x2="288" y2="366" stroke="#c9760a" strokeWidth="3" />
                    </g>
                    <circle cx="288" cy="344" r="30" fill="none" stroke={BLUE} strokeWidth="3" style={{ animation: 'lf-pulse 2.6s ease-out infinite', transformBox: 'fill-box', transformOrigin: 'center' }} />
                    <g style={{ animation: 'lf-pop .5s ease-out .3s both', transformBox: 'fill-box', transformOrigin: 'center' }}>
                        <circle cx="314" cy="322" r="14" fill="#22b257" />
                        <path d="M308 322 l4 4 l8 -9" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </g>

                    {/* Open-body flatbed truck — stake rails + visible cargo, cab on the right */}
                    <g style={{ animation: 'lf-drive 7s ease-in-out infinite', transformBox: 'fill-box', transformOrigin: 'center' }}>
                        <rect x="336" y="330" width="114" height="10" rx="2" fill={NAVY} />
                        <line x1="336" y1="288" x2="450" y2="288" stroke={NAVY} strokeWidth="4" strokeLinecap="round" />
                        {[344, 368, 392, 416, 440].map(x => (
                            <line key={x} x1={x} y1="288" x2={x} y2="332" stroke={NAVY} strokeWidth="4" strokeLinecap="round" />
                        ))}
                        {/* Always-on cargo; the loading-dock box (below) flies in next to it and triggers departure */}
                        <rect x="394" y="296" width="28" height="36" rx="3" fill={BLUE} />
                        <path d="M454 292 h34 l20 24 v14 h-54 z" fill={BLUE} />
                        <path d="M458 300 h24 l14 18 h-38 z" fill="#cfe6f6" />
                        <rect x="450" y="330" width="64" height="10" rx="2" fill={NAVY} />
                        <rect x="506" y="322" width="8" height="12" rx="2" fill={NAVY} />
                        <g style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: 'lf-roll 7s ease-in-out infinite' }}>
                            <circle cx="376" cy="352" r="18" fill={NAVY} /><circle cx="376" cy="352" r="7" fill="#dce4ec" />
                        </g>
                        <g style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: 'lf-roll 7s ease-in-out infinite' }}>
                            <circle cx="478" cy="352" r="18" fill={NAVY} /><circle cx="478" cy="352" r="7" fill="#dce4ec" />
                        </g>
                        <line x1="316" y1="300" x2="336" y2="300" stroke={BLUE} strokeWidth="3" strokeLinecap="round" opacity=".5" />
                        <line x1="308" y1="316" x2="332" y2="316" stroke={ORANGE} strokeWidth="3" strokeLinecap="round" opacity=".5" />
                        <line x1="318" y1="332" x2="336" y2="332" stroke={BLUE} strokeWidth="3" strokeLinecap="round" opacity=".5" />
                    </g>
                </svg>
            </div>

            {/* Spinner keyframes */}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}
